import React from "react";
import {
    BarRow,
    Card,
    EmptyState,
    SERIES_COLORS,
    formatCurrency,
    formatPercent,
} from "./Primitives";

function DistributionList({ items, emptyMessage }) {
    if (!items || items.length === 0) {
        return <EmptyState message={emptyMessage} />;
    }

    const max = Math.max(...items.map((item) => item.percentage), 1);

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {items.map((item, index) => (
                <BarRow
                    key={item.value}
                    label={item.value}
                    value={item.percentage}
                    max={max}
                    color={SERIES_COLORS[index % SERIES_COLORS.length]}
                    labelWidth={118}
                    displayWidth={30}
                    display={item.count}
                    details={[
                        {
                            color: SERIES_COLORS[index % SERIES_COLORS.length],
                            label: "stays",
                            value: item.count,
                        },
                        { label: "share of stays", value: formatPercent(item.percentage) },
                    ]}
                    trailing={
                        <span
                            style={{ fontSize: 12, fontWeight: 700, color: "#1a1a2e", width: 42, textAlign: "right" }}
                        >
                            {formatPercent(item.percentage)}
                        </span>
                    }
                />
            ))}
        </div>
    );
}

const FOOD_LABELS = {
    VEG: "Vegetarian",
    NON_VEG: "Non-vegetarian",
    EGG: "Egg",
};

const FOOD_COLORS = {
    VEG: "#16a34a",
    NON_VEG: "#FF7378",
    EGG: "#d97706",
};

export default function GuestsTab({ guests }) {
    const bookings = guests?.bookings ?? 0;
    const food = guests?.food_preference ?? [];
    const foodTotal = food.reduce((sum, row) => sum + row.revenue, 0);
    const maxFood = Math.max(...food.map((row) => row.revenue), 1);

    const sharedExtra = <span style={{ fontSize: 11, color: "#9097ac" }}>{bookings} stays</span>;

    return (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            <Card title="Trip Purpose" extra={sharedExtra}>
                <DistributionList items={guests?.trip_purpose} emptyMessage="Not captured for these stays" />
            </Card>

            <Card title="Travelling With" extra={sharedExtra}>
                <DistributionList items={guests?.travelling_with} emptyMessage="Not captured for these stays" />
            </Card>

            <Card title="Dietary Preferences" extra={sharedExtra}>
                <DistributionList items={guests?.dietary} emptyMessage="Not captured for these stays" />
            </Card>

            <Card
                title="Interests During Stay"
                extra={<span style={{ fontSize: 11, color: "#9097ac" }}>Multi-select · share of stays</span>}
            >
                <DistributionList items={guests?.interests} emptyMessage="Not captured for these stays" />
            </Card>

            <Card title="Arrival Mode" extra={sharedExtra}>
                <DistributionList items={guests?.arrival_mode} emptyMessage="Not captured for these stays" />
            </Card>

            <Card
                title="Food Orders · Veg vs Non-Veg"
                extra={<span style={{ fontSize: 11, color: "#9097ac" }}>{formatCurrency(foodTotal)} total</span>}
            >
                {food.length === 0 ? (
                    <EmptyState message="No food orders in this period" />
                ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: 13 }}>
                        {food.map((row) => (
                            <div key={row.food_type}>
                                <div
                                    style={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "baseline",
                                        marginBottom: 5,
                                    }}
                                >
                                    <span style={{ fontSize: 13, color: "#1a1a2e" }}>
                                        {FOOD_LABELS[row.food_type] || row.food_type}
                                    </span>
                                    <span style={{ fontSize: 12, color: "#9097ac" }}>
                                        {row.orders} orders · {row.items} items
                                    </span>
                                </div>
                                <BarRow
                                    label={FOOD_LABELS[row.food_type] || row.food_type}
                                    value={row.revenue}
                                    max={maxFood}
                                    color={FOOD_COLORS[row.food_type] || "#9097ac"}
                                    labelWidth={0}
                                    displayWidth={74}
                                    display={formatCurrency(row.revenue)}
                                    details={[
                                        {
                                            color: FOOD_COLORS[row.food_type] || "#9097ac",
                                            label: "revenue",
                                            value: formatCurrency(row.revenue),
                                        },
                                        { label: "orders", value: row.orders },
                                        { label: "items", value: row.items },
                                        { label: "share", value: formatPercent(row.percentage) },
                                    ]}
                                    trailing={
                                        <span
                                            style={{
                                                fontSize: 12.5,
                                                fontWeight: 700,
                                                color: FOOD_COLORS[row.food_type] || "#9097ac",
                                                width: 42,
                                                textAlign: "right",
                                            }}
                                        >
                                            {formatPercent(row.percentage)}
                                        </span>
                                    }
                                />
                            </div>
                        ))}
                    </div>
                )}
            </Card>
        </div>
    );
}
